// ---------------------------------------------------------------------------
// BrandSplashPreviewPage — 3 variants of the "Living Gradient" splash screen.
// All share: animated gradient bg, warm ember glow, floating logo.
// Each variant has a different animation on the "Connecting" text.
//
// Route: /brand-preview  (isolated — does not touch real splash screen)
// ---------------------------------------------------------------------------
import { useState } from 'react';

const LOGO = '/Defensible-Connect-Logo-WEB-768px.png';
const WORD = 'Connecting';
const LETTERS = WORD.split('');

type VariantId = 'A' | 'B' | 'C';

interface Variant {
  id: VariantId;
  label: string;
  desc: string;
  portalName: string;
}

const VARIANTS: Variant[] = [
  {
    id: 'A',
    label: 'Wave Flag',
    desc: 'Each letter ripples up and down with staggered timing — like a flag blowing in the wind. Continuous, flowing, alive.',
    portalName: 'System Admin Portal',
  },
  {
    id: 'B',
    label: 'Traveling Pulse',
    desc: 'A glow travels through each letter one by one in a never-ending loop. Like a signal moving through a wire — "connecting" in motion.',
    portalName: 'Tenant Portal',
  },
  {
    id: 'C',
    label: 'Shimmer Flow',
    desc: 'A band of light continuously sweeps across the word, giving it a polished, premium feel. Smooth and hypnotic.',
    portalName: 'Mobile Field App',
  },
];

// ---------- Per-letter "Connecting" with wave flag animation ----------
function WaveText() {
  return (
    <span className="bsp-word" aria-label={WORD}>
      {LETTERS.map((ch, i) => (
        <span
          key={i}
          className="bsp-letter bsp-letter--wave"
          style={{ animationDelay: `${1.6 + i * 0.12}s` }}
        >
          {ch}
        </span>
      ))}
    </span>
  );
}

// ---------- Per-letter "Connecting" with traveling pulse ----------
function PulseText() {
  return (
    <span className="bsp-word" aria-label={WORD}>
      {LETTERS.map((ch, i) => (
        <span
          key={i}
          className="bsp-letter bsp-letter--pulse"
          style={{ animationDelay: `${1.6 + i * 0.18}s` }}
        >
          {ch}
        </span>
      ))}
    </span>
  );
}

// ---------- Whole-word "Connecting" with shimmer sweep ----------
function ShimmerText() {
  return (
    <span className="bsp-word bsp-word--shimmer" aria-label={WORD}>
      {WORD}
    </span>
  );
}

// ---------- Shared splash shell (ember glow + floating logo) ----------
function SplashShell({ portalName, textVariant }: { portalName: string; textVariant: 'wave' | 'pulse' | 'shimmer' }) {
  return (
    <div className="bsp-screen bsp-bg-gradient">
      <div className="bsp-glow bsp-glow--ember" />
      <div className="bsp-logo-wrap bsp-enter--scale">
        <img src={LOGO} alt="Defensible Connect" className="bsp-logo bsp-anim--float" draggable={false} />
      </div>
      <div className="bsp-text-block bsp-fade-1">
        {textVariant === 'wave' && <WaveText />}
        {textVariant === 'pulse' && <PulseText />}
        {textVariant === 'shimmer' && <ShimmerText />}
      </div>
      <div className="bsp-status bsp-fade-2">{portalName}</div>
      <div className="bsp-bar"><div className="bsp-bar-inner" /></div>
    </div>
  );
}

export default function BrandSplashPreviewPage() {
  const [active, setActive] = useState<VariantId>('A');
  const v = VARIANTS.find((x) => x.id === active)!;

  const textVariant = active === 'A' ? 'wave' : active === 'B' ? 'pulse' : 'shimmer';

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 99999, fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
      {/* Tab bar */}
      <div className="bsp-tabs">
        {VARIANTS.map((variant) => (
          <button
            key={variant.id}
            className={`bsp-tab ${active === variant.id ? 'bsp-tab--active' : ''}`}
            onClick={() => setActive(variant.id)}
          >
            <span className="bsp-tab-id">{variant.id}</span>
            <span className="bsp-tab-label">{variant.label}</span>
          </button>
        ))}
      </div>

      {/* Description bar */}
      <div className="bsp-desc">{v.desc}</div>

      {/* Active splash — key forces re-mount for entrance animations */}
      <div className="bsp-stage" key={active}>
        <SplashShell portalName={v.portalName} textVariant={textVariant as 'wave' | 'pulse' | 'shimmer'} />
      </div>

      <style>{`
        /* ================================================================== */
        /* TAB BAR                                                            */
        /* ================================================================== */
        .bsp-tabs {
          position: fixed; top: 0; left: 0; right: 0; z-index: 100000;
          display: flex; gap: 2px;
          background: rgba(2,6,23,0.85); backdrop-filter: blur(12px);
          padding: 8px 16px; border-bottom: 1px solid rgba(255,255,255,0.08);
        }
        .bsp-tab {
          display: flex; align-items: center; gap: 8px;
          padding: 8px 18px; border-radius: 8px;
          background: transparent; border: 1px solid rgba(255,255,255,0.1);
          color: rgba(255,255,255,0.6); cursor: pointer;
          font-size: 13px; font-weight: 600; transition: all 0.25s ease;
        }
        .bsp-tab:hover { background: rgba(255,255,255,0.06); color: rgba(255,255,255,0.85); }
        .bsp-tab--active {
          background: rgba(255,255,255,0.12); border-color: rgba(255,255,255,0.25);
          color: #fff;
        }
        .bsp-tab-id {
          display: inline-flex; align-items: center; justify-content: center;
          width: 22px; height: 22px; border-radius: 6px;
          background: rgba(255,255,255,0.1); font-size: 11px; font-weight: 800;
        }
        .bsp-tab--active .bsp-tab-id { background: rgba(124,194,66,0.5); }
        .bsp-tab-label { white-space: nowrap; }

        .bsp-desc {
          position: fixed; bottom: 0; left: 0; right: 0; z-index: 100000;
          padding: 10px 24px; text-align: center;
          background: rgba(2,6,23,0.85); backdrop-filter: blur(12px);
          border-top: 1px solid rgba(255,255,255,0.08);
          color: rgba(255,255,255,0.7); font-size: 13px; font-weight: 500;
        }

        /* ================================================================== */
        /* STAGE                                                              */
        /* ================================================================== */
        .bsp-stage {
          position: fixed; inset: 0; z-index: 99998;
          animation: bsp-fadeIn 0.6s ease both;
        }

        /* ================================================================== */
        /* SHARED STRUCTURE (same on all 3 variants)                          */
        /* ================================================================== */
        .bsp-screen {
          position: absolute; inset: 0;
          display: flex; flex-direction: column;
          align-items: center; justify-content: center;
          overflow: hidden; color: #f1f5f9;
        }

        /* Living gradient background */
        .bsp-bg-gradient {
          background: linear-gradient(135deg, #0a1628 0%, #132e4a 30%, #1a4a5e 55%, #2d6b4f 80%, #3a7d3e 100%);
          background-size: 300% 300%;
          animation: bsp-bgShift 12s ease-in-out infinite;
        }

        .bsp-logo-wrap {
          position: relative; z-index: 2;
          width: min(82vw, 520px);
          overflow: hidden; border-radius: 8px;
        }
        .bsp-logo {
          display: block; width: 100%; height: auto;
          user-select: none; pointer-events: none;
        }

        /* "Connecting" text block */
        .bsp-text-block {
          position: relative; z-index: 2;
          margin-top: 28px;
          text-align: center;
        }
        .bsp-word {
          display: inline-flex;
          font-size: clamp(16px, 2.4vw, 22px);
          font-weight: 700;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: rgba(241,245,249,0.92);
        }
        .bsp-letter {
          display: inline-block;
          will-change: transform, opacity;
        }

        /* Portal label below */
        .bsp-status {
          position: relative; z-index: 2;
          margin-top: 8px;
          font-size: clamp(11px, 1.4vw, 13px);
          font-weight: 500; letter-spacing: 0.12em;
          text-transform: uppercase;
          color: rgba(148,163,184,0.85);
        }
        .bsp-fade-1 { animation: bsp-textFadeUp 1s ease 1.4s both; }
        .bsp-fade-2 { animation: bsp-textFadeUp 1s ease 1.7s both, bsp-statusPulse 4s ease-in-out 2.8s infinite; }

        .bsp-bar {
          position: fixed; bottom: 34px; left: 0; right: 0; height: 2px;
          z-index: 3; overflow: hidden;
          background: rgba(255,255,255,0.05);
        }
        .bsp-bar-inner {
          height: 100%; width: 40%;
          background: linear-gradient(90deg, transparent, rgba(124,194,66,0.5) 40%, rgba(180,220,130,0.7) 50%, rgba(124,194,66,0.5) 60%, transparent);
          animation: bsp-barSweep 3s ease-in-out 2s infinite;
        }
        .bsp-glow {
          position: absolute; z-index: 1; border-radius: 50%;
          pointer-events: none;
        }

        /* ================================================================== */
        /* WARM EMBER GLOW (shared logo treatment)                            */
        /* ================================================================== */
        .bsp-glow--ember {
          width: 750px; height: 750px;
          top: 50%; left: 50%; transform: translate(-50%, -50%);
          background: radial-gradient(circle,
            rgba(200,160,60,0.12) 0%,
            rgba(180,120,40,0.08) 20%,
            rgba(124,194,66,0.10) 40%,
            transparent 65%
          );
          animation: bsp-emberPulse 5s ease-in-out infinite;
        }

        .bsp-enter--scale {
          animation: bsp-enterScale 1.4s cubic-bezier(0.16, 1, 0.3, 1) both;
        }

        .bsp-anim--float {
          filter: drop-shadow(0 20px 60px rgba(10,22,40,0.45));
          animation: bsp-logoFloat 5s ease-in-out 1.5s infinite;
        }

        /* ================================================================== */
        /* TEXT ANIMATION A — Wave Flag                                        */
        /* ================================================================== */
        .bsp-letter--wave {
          animation: bsp-waveFlag 2.4s ease-in-out infinite;
        }
        @keyframes bsp-waveFlag {
          0%, 100% { transform: translateY(0)    rotate(0deg); }
          25%      { transform: translateY(-6px) rotate(-2deg); }
          75%      { transform: translateY(3px)  rotate(1deg); }
        }

        /* ================================================================== */
        /* TEXT ANIMATION B — Traveling Pulse                                  */
        /* ================================================================== */
        .bsp-letter--pulse {
          opacity: 0.45;
          animation: bsp-travelPulse 2.2s ease-in-out infinite;
        }
        @keyframes bsp-travelPulse {
          0%, 100% {
            opacity: 0.45;
            transform: scale(1);
            text-shadow: 0 0 0 transparent;
          }
          50% {
            opacity: 1;
            transform: scale(1.12);
            text-shadow: 0 0 18px rgba(124,194,66,0.6), 0 0 40px rgba(124,194,66,0.25);
          }
        }

        /* ================================================================== */
        /* TEXT ANIMATION C — Shimmer Flow                                     */
        /* ================================================================== */
        .bsp-word--shimmer {
          background: linear-gradient(
            105deg,
            rgba(241,245,249,0.55) 0%,
            rgba(241,245,249,0.55) 35%,
            rgba(255,255,255,1)    48%,
            rgba(200,230,160,1)    50%,
            rgba(255,255,255,1)    52%,
            rgba(241,245,249,0.55) 65%,
            rgba(241,245,249,0.55) 100%
          );
          background-size: 250% 100%;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: bsp-textShimmer 3s ease-in-out 1.6s infinite;
        }
        @keyframes bsp-textShimmer {
          0%   { background-position: 150% center; }
          100% { background-position: -50% center; }
        }

        /* ================================================================== */
        /* KEYFRAMES — Shared                                                 */
        /* ================================================================== */
        @keyframes bsp-fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes bsp-textFadeUp {
          0%   { opacity: 0; transform: translateY(12px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes bsp-statusPulse {
          0%, 100% { opacity: 0.65; }
          50%      { opacity: 1; }
        }
        @keyframes bsp-barSweep {
          0%   { transform: translateX(-140%); }
          100% { transform: translateX(340%); }
        }
        @keyframes bsp-bgShift {
          0%, 100% { background-position: 0% 50%; }
          33%      { background-position: 100% 30%; }
          66%      { background-position: 50% 80%; }
        }
        @keyframes bsp-enterScale {
          0%   { opacity: 0; transform: scale(0.88) translateY(24px); filter: blur(8px); }
          100% { opacity: 1; transform: scale(1)    translateY(0);    filter: blur(0); }
        }
        @keyframes bsp-logoFloat {
          0%, 100% {
            transform: translateY(0);
            filter: drop-shadow(0 20px 60px rgba(10,22,40,0.45));
          }
          50% {
            transform: translateY(-10px);
            filter: drop-shadow(0 30px 80px rgba(10,22,40,0.35));
          }
        }
        @keyframes bsp-emberPulse {
          0%, 100% {
            opacity: 0.4;
            transform: translate(-50%, -50%) scale(0.9);
          }
          50% {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1.25);
          }
        }
      `}</style>
    </div>
  );
}
